import { mat4 } from "gl-matrix";
import moment from "moment-timezone";

export class Camera {
  private projectionMatrix: mat4 = mat4.create();

  private xRange: number[];
  private yRange: number[];
  private zRange: number[];

  private readonly minZoom: number = moment.duration(1, "hours").asMilliseconds();
  private readonly maxZoom: number = moment.duration(3, "months").asMilliseconds();

  public static readonly referenceTime = new Date().getTime();

  constructor(xRange: number[], yRange: number[]) {
    if (xRange.length !== 2) throw new Error("xRange must only contain min/max!");
    if (yRange.length !== 2) throw new Error("yRange must only contain min/max!");
    this.xRange = xRange;
    this.yRange = yRange;
    this.zRange = [1, 1000];
    this.updateProjectionMatrix();
  }

  public getXRange = (): number[] => {
    return this.xRange;
  };

  public setXRange = (range: number[]): void => {
    if (range.length !== 2) throw new Error("xRange must only contain min/max!");
    //check for the min or max range...
    const delta = range[1] - range[0];
    if (delta < this.minZoom || delta > this.maxZoom) {
      const midPoint = range[0] + delta * 0.5;
      const newRange =
        delta > this.maxZoom
          ? [midPoint - 0.5 * this.maxZoom, midPoint + 0.5 * this.maxZoom]
          : [midPoint - 0.5 * this.minZoom, midPoint + 0.5 * this.minZoom];
      this.xRange = newRange;
    } else {
      this.xRange = range;
    }
  };

  public panX = (delta: number): void => {
    const [minX, maxX] = this.xRange;
    this.setXRange([minX + delta, maxX + delta]);
  };

  public panY = (delta: number): void => {
    const [minY, maxY] = this.yRange;
    this.setYRange([minY + delta, maxY + delta]);
  };

  public setYRange = (range: number[]): void => {
    if (range.length !== 2) throw new Error("yRange must only contain min/max!");
    this.yRange = range;
  };

  public getYRange = (): number[] => {
    return this.yRange;
  };

  public zoom = (zoomDelta: number): void => {
    const [minX, maxX] = this.xRange;
    const [min, max] = [minX - zoomDelta, maxX + zoomDelta];
    const delta = max - min;
    if (delta > this.minZoom && delta < this.maxZoom) {
      this.setXRange([min, max]);
    }
  };

  public get currentZoom(): number {
    return this.xRange[1] - this.xRange[0];
  }

  public timeAxisToPixelCoords = (time: number, width: number): number => {
    const [minTime, maxTime] = this.getXRange();
    const deltaTotalTime = maxTime - minTime;
    const deltaTime = time - minTime;
    return width * (deltaTime / deltaTotalTime);
  };

  public pixelCoordsToTimeAxis = (pixels: number, width: number): number => {
    const [minTime, maxTime] = this.getXRange();
    //something happens here that is causing what is retuned to be NaN
    if (width <= 0) {
      console.error(
        "pixelCoordsToTimeAxis: trying to divide by a canvas width that is 0 - make sure canvas is mounted bedfore calling this"
      );
    }
    const percent = pixels / width;
    return percent * (maxTime - minTime);
  };

  public pixelCoordsToEpochTime = (pixels: number, width: number): number => {
    const [minTime, maxTime] = this.getXRange();
    const percent = pixels / width;
    if (width <= 0) {
      console.error(
        "pixelCoordsToEpochTime: trying to divide by a canvas width that is 0 - make sure canvas is mounted bedfore calling this"
      );
    }
    return percent * (maxTime - minTime) + minTime;
  };

  public getProjectionMatrix(gl: WebGLRenderingContext): mat4 {
    return this.projectionMatrix;
  }

  public updateProjectionMatrix = (): void => {
    mat4.identity(this.projectionMatrix);
    const [xMin, xMax] = this.xRange;
    const [yMin, yMax] = this.yRange;
    const [zMin, zMax] = this.zRange;
    mat4.ortho(this.projectionMatrix, xMin - Camera.referenceTime, xMax - Camera.referenceTime, yMax, yMin, zMin, zMax);
  };
}
